import React from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Divider, Link, Skeleton, Stack, TextField, Typography } from '@mui/material';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import { CenteredPage, CodeField, Surface, ToolbarButton } from '../../ui-kit';
import { BrandLockup } from '../onboarding/components/OnboardingLayout';
import ManualDashboardPage from '../manual-dashboard/ManualDashboardPage';
import LookupDashboardPage from '../manual-dashboard/lookup/LookupDashboardPage';
import PlannerDashboardPage from '../manual-dashboard/planner/PlannerDashboardPage';
import ReportDashboardPage from '../manual-dashboard/report/ReportDashboardPage';
import { SharedBoardProvider } from '../manual-dashboard/sharedBoard';
import { BrandScope } from '../../theme/ThemeProvider';
import type { TenantBranding } from '../../types/branding';
import { setSharedBoardTarget } from '../../services/manualDashboardService';
import {
  fetchSharedTarget, forgetViewerSession, openSharedLink, requestShareCode, saveViewerSession, savedViewerSession,
  sharedDashboardBase, verifyShareCode, type SharedTarget,
} from '../../services/sharingService';

type Step = 'checking' | 'dead' | 'email' | 'code' | 'open';

/**
 * What someone opening a shared link sees: /shared/<link>.
 *
 * Nothing until they prove an email with a 6-digit code; then the one shared
 * thing, read-only and live, and nothing else of the workspace. Their sign-in
 * is kept for this link in this tab only and never touches a workspace login.
 */
export default function SharedBoardPage() {
  const { link = '' } = useParams<{ link: string }>();
  const navigate = useNavigate();
  const [step, setStep] = React.useState<Step>('checking');
  const [workspace, setWorkspace] = React.useState('');
  // The sharing workspace's look: the page wears its brand, not this deployment's.
  const [branding, setBranding] = React.useState<TenantBranding | null>(null);
  // What is being shared and by whom, so the person knows what they are opening.
  const [about, setAbout] = React.useState<{ name: string | null; sharedBy: string | null }>({ name: null, sharedBy: null });
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [note, setNote] = React.useState<string | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [target, setTarget] = React.useState<SharedTarget | null>(null);

  const open = React.useCallback(async (token: string, who: string) => {
    const found = await fetchSharedTarget(link, token);
    setSharedBoardTarget({ base: sharedDashboardBase(link), token });
    setTarget({ ...found, viewer: found.viewer || who });
    setStep('open');
    if (found.dashboard) navigate(`board/${found.dashboard.dashboard_id}`, { replace: true });
  }, [link, navigate]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opened = await openSharedLink(link);
        if (cancelled) return;
        setWorkspace(opened.workspace);
        setAbout({ name: opened.name, sharedBy: opened.shared_by });
        setBranding(opened.branding && opened.branding.colors ? opened.branding : null);
        if (opened.name) document.title = `${opened.name} · ${opened.workspace}`;
        const saved = savedViewerSession(link);
        if (saved) {
          try {
            await open(saved.token, saved.email);
            return;
          } catch {
            forgetViewerSession(link);
          }
        }
        if (!cancelled) setStep('email');
      } catch (err) {
        if (!cancelled) {
          setProblem(err instanceof Error ? err.message : 'This link has expired or been turned off.');
          setStep('dead');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [link, open]);

  // Whatever the board's calls were pointed at, they go back when this page closes.
  React.useEffect(() => () => setSharedBoardTarget(null), []);

  const sendCode = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const sent = await requestShareCode(link, email);
      setNote(sent.message);
      setCode('');
      setStep('code');
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not send a code.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (full: string) => {
    if (busy || full.length !== 6) return;
    setBusy(true);
    setProblem(null);
    try {
      const session = await verifyShareCode(link, email, full);
      saveViewerSession(link, session);
      await open(session.token, session.email);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That code is not right.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => {
    forgetViewerSession(link);
    setSharedBoardTarget(null);
    setTarget(null);
    setCode('');
    setStep('email');
    navigate(`/shared/${link}`, { replace: true });
  };

  if (step === 'open' && target) {
    return (
      <BrandScope branding={branding}>
      <Box sx={(t) => ({ minHeight: '100vh', bgcolor: t.tokens.surface.canvas })}>
        <Box
          component="header"
          sx={(t) => ({
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: { xs: 2, md: 3 }, py: 1.25,
            borderBottom: `1px solid ${t.tokens.border.subtle}`, bgcolor: t.tokens.surface.raised,
          })}
        >
          <BrandLockup />
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, flex: 1, minWidth: 0 })}>
            Read-only view
          </Typography>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, overflowWrap: 'anywhere' })}>
            Viewing as {target.viewer}
          </Typography>
          <ToolbarButton variant="ghost" onClick={signOut}>Sign out</ToolbarButton>
        </Box>
        <Box component="main" sx={{ px: { xs: 2, md: 3 }, py: 2, maxWidth: 1600, mx: 'auto' }}>
          <SharedBoardProvider viewer={target.viewer} allowExport={target.allow_export}>
            <Routes>
              <Route path="board/:dashboardId" element={<SharedBoard target={target} />} />
              <Route path="*" element={target.dashboard
                ? <Navigate to={`board/${target.dashboard.dashboard_id}`} replace /> : null} />
            </Routes>
          </SharedBoardProvider>
        </Box>
      </Box>
      </BrandScope>
    );
  }

  const what = about.name ?? 'A dashboard';
  return (
    <BrandScope branding={branding}>
    <CenteredPage brand={<BrandLockup />} maxWidth={440} align="middle">
      <Surface sx={{ p: { xs: 3, md: 4 } }}>
        {step === 'checking' ? (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" width={44} height={44} />
            <Skeleton variant="text" width="70%" height={32} />
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={36} />
          </Stack>
        ) : step === 'dead' ? (
          <Stack spacing={1.5}>
            <Typography variant="h4">This link no longer opens</Typography>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{problem}</Typography>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            {/* What is shared, and by whom: the same on both steps, so the
                page does not change under the person as they sign in. */}
            <Stack spacing={1.5}>
              <Box sx={(t) => ({
                width: 44, height: 44, borderRadius: `${t.ds.radius.md}px`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: t.tokens.brand.primarySoft, color: t.tokens.brand.primary,
              })}>
                <InsightsRounded />
              </Box>
              <Box>
                <Typography variant="overline" sx={(t) => ({ color: t.tokens.text.tertiary })}>Shared with you</Typography>
                <Typography variant="h3" component="h1" sx={{ overflowWrap: 'anywhere' }}>{what}</Typography>
                <Typography variant="body2" sx={(t) => ({ mt: 0.75, color: t.tokens.text.secondary })}>
                  {about.sharedBy ? `${about.sharedBy} at ${workspace}` : workspace} shared this dashboard with you.
                </Typography>
              </Box>
            </Stack>

            <Divider />

            {step === 'email' ? (
              <Stack component="form" spacing={1.5}
                onSubmit={(e: React.FormEvent) => { e.preventDefault(); void sendCode(); }}>
                <Typography variant="subtitle1">Confirm your email to open it</Typography>
                {problem ? <Alert severity="error">{problem}</Alert> : null}
                <TextField id="shared-email" label="Email" type="email" autoComplete="email" autoFocus size="small"
                  value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
                <ToolbarButton variant="brand" size="medium" loading={busy} disabled={!email.trim()}
                  onClick={() => { void sendCode(); }}>
                  Send code
                </ToolbarButton>
                <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                  We email you a 6-digit code. No password or account needed.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle1">Enter your code</Typography>
                  <Typography variant="body2" sx={(t) => ({ mt: 0.5, color: t.tokens.text.secondary })}>
                    {note ?? 'If this email can open the link, a code is on its way.'} Sent to{' '}
                    <Box component="span" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600, overflowWrap: 'anywhere' })}>
                      {email}
                    </Box>.
                  </Typography>
                </Box>
                {problem ? <Alert severity="error">{problem}</Alert> : null}
                <CodeField value={code} onChange={setCode} onComplete={(full) => { void verify(full); }}
                  error={Boolean(problem)} disabled={busy} autoFocus />
                <ToolbarButton variant="brand" size="medium" loading={busy} disabled={code.length !== 6}
                  onClick={() => { void verify(code); }}>
                  Open dashboard
                </ToolbarButton>
                <Stack direction="row" justifyContent="space-between" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                  <Link component="button" type="button" variant="body2"
                    onClick={() => { setProblem(null); setStep('email'); }}>
                    Use a different email
                  </Link>
                  <Link component="button" type="button" variant="body2" disabled={busy}
                    onClick={() => { void sendCode(); }}>
                    Send a new code
                  </Link>
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
      </Surface>
    </CenteredPage>
    </BrandScope>
  );
}

/** The shared board itself, drawn by the same page the workspace uses for it. */
function SharedBoard({ target }: { target: SharedTarget }) {
  const kind = target.dashboard?.board_kind;
  const view = target.options?.view;
  if (kind === 'report') return <ReportDashboardPage />;
  if (kind === 'lookup') return <LookupDashboardPage />;
  if (kind === 'planner') {
    return view === 'campaigns' || view === 'simulator'
      ? <PlannerDashboardPage key={view} view={view} />
      : <PlannerDashboardPage />;
  }
  return <ManualDashboardPage />;
}
